import React, {useCallback, useEffect, useState} from 'react';
import {FlatList, Text, View} from 'react-native';
import NoResults from 'src/common-components/noResults';
import {Typography} from 'src/styles';
import {withDatabase} from '@nozbe/watermelondb/react';
import withState from 'src/redux/wrapper';
import {withObservables} from '@nozbe/watermelondb/react';
import {Q} from '@nozbe/watermelondb';
import {of} from 'rxjs';
import {compose} from 'recompose';
import moment from 'moment';
import {useStyle} from 'src/providers/style';
import {useTranslations} from 'src/providers/translation';
import {useDatabase} from '@nozbe/watermelondb/react';
import {endOfDay, startOfDay} from 'date-fns';
import Spinner from 'react-native-loading-spinner-overlay';
import {Patient, Session, User} from 'src/models';
import {Value as Location} from 'src/hook-form-inputs/location';
import {cptCodes} from 'src/hook-form-inputs/cpt';
import {rrulestr} from 'rrule';

const TimesheetClients = ({
  staff = undefined,
  showArchived = false,
  filters,
  setTimesheetItems,
  profile,
  role,
  assignedClients,
}: any) => {
  const database = useDatabase();
  const styles = useStyle();
  const translations = useTranslations();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any>([]);
  const [totalDuration, setTotalDuration] = useState<number>(0);

  const clientTabsTitles = [
    translations('service_date'),
    translations('service_time'),
    translations('staff_name'),
    translations('npi'),
    translations('client_name'),
    translations('identifier'),
    translations('cpt'),
    translations('place_of_service'),
    translations('status'),
    translations('time_worked_hrs'),
    translations('time_worked_mins'),
    translations('units_of_service'),
  ];

  const recalculateAppointments = async () => {
    const locationQuery = [];
    let curatedDuration = 0;
    if (filters?.locations && filters.locations.length) {
      locationQuery.push(Q.where('location', Q.oneOf(filters.locations)));
    }

    const clients = [];
    for (const client of filters.clients) {
      clients.push(client);
    }

    let clientQueries: [Q.On[]] | any[] = [];
    if (clients.length) {
      clientQueries = [Q.where('patient_id', Q.oneOf(clients))];
    } else if (role?.patientAssignedOnly) {
      const assignedClientIds = assignedClients?.map(
        (client: any) => client.id,
      );
      clientQueries = [Q.where('patient_id', Q.oneOf(assignedClientIds))];
    } else {
      clientQueries = [];
    }

    const statusQuery = [];
    if (filters?.status && filters.status.length) {
      const statuses = [];
      for (const status of filters.status) {
        if (status === 'not-started') {
          statuses.push(Q.where('start_timestamp', Q.eq(null)));
        } else if (status === 'in-progress') {
          statuses.push(
            Q.and(
              Q.where('start_timestamp', Q.notEq(null)),
              Q.where('end_timestamp', Q.eq(null)),
            ),
          );
        } else if (status === 'missing-values') {
          statuses.push(
            Q.and(
              Q.where('end_timestamp', Q.notEq(null)),
              Q.where('submission_timestamp', Q.eq(null)),
            ),
          );
        } else if (status === 'completed') {
          statuses.push(Q.where('submission_timestamp', Q.notEq(null)));
        }
      }
      statusQuery.push(Q.or(...statuses));
    }

    const queriedAppointments = await database
      .get('appointments')
      .query(
        Q.where('_partition', profile?.partition),
        Q.or(
          Q.and(
            Q.where('rrule', ''),
            Q.where('date', Q.gte(startOfDay(filters.startDate).getTime())),
            Q.where('date', Q.lte(endOfDay(filters.endDate).getTime())),
            Q.where('deleted_at', null),
          ),
          Q.and(
            Q.where('rrule', Q.notEq('')),
            Q.where('deleted_at', null),
            Q.or(
              Q.and(
                Q.where(
                  'start_timestamp',
                  Q.gte(startOfDay(filters.startDate).getTime()),
                ),
                Q.where(
                  'start_timestamp',
                  Q.lte(filters.endDate.getTime() + 1),
                ),
              ),
              Q.and(
                Q.where(
                  'start_timestamp',
                  Q.lte(startOfDay(filters.startDate).getTime()),
                ),
                Q.or(
                  Q.where('end_timestamp', Q.eq(0)),
                  Q.where(
                    'end_timestamp',
                    Q.gte(startOfDay(filters.startDate).getTime()),
                  ),
                ),
              ),
            ),
          ),
        ),
        Q.on('participants', Q.and(Q.where('deleted_at', null))),
        ...locationQuery,
      );

    let sessionsQueried: any[] = [];

    for (const appointment of queriedAppointments) {
      const staffParticipants = await appointment.staffParticipants.fetch();
      if (appointment.rrule) {
        const rruleSet = rrulestr(appointment.rrule, {
          forceset: true,
        });
        // Accommodate Daylight Savings
        let appointmentEndDate = endOfDay(filters.endDate);
        let appointmentStartDate = startOfDay(filters.startDate);
        // Always use UTC versions of the start and end window
        const startTime = new Date(
          Date.UTC(
            appointmentStartDate.getUTCFullYear(),
            appointmentStartDate.getUTCMonth(),
            appointmentStartDate.getUTCDate(),
            0,
            0,
            0,
          ),
        );
        const endTime = new Date(
          Date.UTC(
            appointmentEndDate.getUTCFullYear(),
            appointmentEndDate.getUTCMonth(),
            appointmentEndDate.getUTCDate(),
            23,
            59,
            59,
          ),
        );

        const dayAfter = new Date(
          appointmentEndDate.getFullYear(),
          appointmentEndDate.getMonth(),
          appointmentEndDate.getDate() + 1,
          0,
          0,
          0,
        );

        const dayBefore = new Date(
          appointmentStartDate.getFullYear(),
          appointmentStartDate.getMonth(),
          appointmentStartDate.getDate() - 1,
          0,
          0,
          0,
        );

        // Get all rrule occurrences in UTC within the date range
        const allOccurrences = rruleSet
          .between(startTime, endTime, true)
          .map(rruleDate => {
            const realDate = new Date(
              rruleDate.getFullYear(),
              rruleDate.getMonth(),
              rruleDate.getDate(),
            );
            realDate.setTime(
              rruleDate.getTime() +
                rruleSet.options.dtstart.getTimezoneOffset() * 60 * 1000,
            );
            return realDate;
          });

        // Convert EXDATEs to ISO strings for exact UTC matching
        const excludedDates = new Set(
          rruleSet.exdates().map(d => d.toISOString().split('T')[0]),
        );

        for (const date of allOccurrences) {
          if (excludedDates.has(date.toISOString().split('T')[0])) {
            continue;
          }

          if (
            date.toISOString().split('T')[0] ===
            dayAfter.toISOString().split('T')[0]
          ) {
            continue;
          }
          if (
            date.toISOString().split('T')[0] ===
            dayBefore.toISOString().split('T')[0]
          ) {
            continue;
          }
          for (const staffParticipant of staffParticipants) {
            if (
              (filters.staff.length > 0 &&
                filters.staff.includes(staffParticipant.userId)) ||
              (filters.staff.length === 0 && !staff) ||
              (staff && staff?.id === staffParticipant.userId)
            ) {
              let queries = [];
              const user = await database
                .get('users')
                .find(staffParticipant?.userId);
              if ((!showArchived && !user?.deletedAt) || showArchived) {
                const credential = user ? (await user.userCredential)[0] : {};
                if (staffParticipant?.supervision) {
                  queries.push(
                    Q.where('type', 'supervision'),
                    Q.sortBy('end_timestamp', Q.desc),
                  );
                } else {
                  queries.push(
                    Q.where('type', 'session'),
                    Q.sortBy('end_timestamp', Q.desc),
                  );
                }
                let apptSessions = await database
                  .get(Session.table)
                  .query(
                    Q.where('appointment_id', appointment.id),
                    Q.where('session_date', moment(date).format('YYYY-MM-DD')),
                    Q.where('deleted_at', null),
                    ...clientQueries,
                    ...statusQuery,
                    ...queries,
                  )
                  .fetch();

                if (apptSessions[0]) {
                  const session = apptSessions[0];
                  let sessionCpt = session?.cpt || [];
                  let participantCpt = staffParticipant?.cpt || [];
                  if (
                    !filters?.types?.length ||
                    filters.types.some((type: string) =>
                      [...sessionCpt, ...participantCpt].includes(type),
                    )
                  ) {
                    let cptLabels = '';
                    if (session?.cpt) {
                      cptLabels = session?.cpt
                        .map((cptCode: string) => {
                          const cptEntry = cptCodes.find(
                            cpt => cpt.value === cptCode,
                          );
                          return cptEntry
                            ? `${cptCode} - ${cptEntry.label}`
                            : cptCode;
                        })
                        .join(', ');
                    }
                    let status = session?.submissionTimestamp
                      ? 'Completed'
                      : session?.endTimestamp && !session?.submissionTimestamp
                      ? 'Missing Values'
                      : !session?.startTimestamp
                      ? 'Not Started'
                      : 'In-Progress';
                    let time =
                      status === 'Not Started'
                        ? 'Not Started'
                        : status === 'In-Progress'
                        ? 'In-Progress'
                        : `${moment(
                            session?.editedStartTimestamp
                              ? session?.editedStartTimestamp
                              : session?.startTimestamp,
                          ).format('hh:mm A')} - ${moment(
                            session?.editedEndTimestamp
                              ? session?.editedEndTimestamp
                              : session?.endTimestamp,
                          ).format('hh:mm A')}`;
                    let client = await database
                      .get(Patient.table)
                      .find(session?.patientId);
                    let startTimestamp = session?.editedStartTimestamp
                      ? session?.editedStartTimestamp
                      : session?.startTimestamp;
                    let endTimestamp = session?.editedEndTimestamp
                      ? session?.editedEndTimestamp
                      : session?.endTimestamp;

                    let duration = endTimestamp
                      ? Math.abs(
                          moment()
                            .set({
                              hour: endTimestamp.getHours(),
                              minute: endTimestamp.getMinutes(),
                              second: endTimestamp.getSeconds(),
                            })
                            .toDate()
                            .getTime() -
                            moment()
                              .set({
                                hour: startTimestamp.getHours(),
                                minute: startTimestamp.getMinutes(),
                                second: startTimestamp.getSeconds(),
                              })
                              .toDate()
                              .getTime(),
                        ) / 36e5
                      : 0;
                    sessionsQueried.push([
                      moment(session?.sessionDate).format('MM/DD/YYYY'),
                      time,
                      user ? `${user?.firstName} ${user?.lastName}` : '',
                      credential?.npi,
                      `${client.firstName} ${client.lastName}`,
                      client.identifier,
                      cptLabels,
                      Location(session?.location),
                      status,
                      parseFloat(duration.toFixed(2)),
                      parseFloat((duration * 60).toFixed(2)),
                      parseFloat(duration.toFixed(2)) * 4,
                    ]);
                    curatedDuration += duration;
                  }
                }
              }
            }
          }
        }
      } else {
        for (const staffParticipant of staffParticipants) {
          if (
            (filters.staff.length > 0 &&
              filters.staff.includes(staffParticipant.userId)) ||
            (filters.staff.length === 0 && !staff) ||
            (staff && staff?.id === staffParticipant.userId)
          ) {
            let queries = [];
            const user = await database
              .get('users')
              .find(staffParticipant?.userId);
            if ((!showArchived && !user?.deletedAt) || showArchived) {
              const credential = user ? (await user.userCredential)[0] : {};

              if (staffParticipant?.supervision) {
                queries.push(
                  Q.where('type', 'supervision'),
                  Q.sortBy('end_timestamp', Q.desc),
                );
              } else {
                queries.push(
                  Q.where('type', 'session'),
                  Q.sortBy('end_timestamp', Q.desc),
                );
              }
              let apptSessions = await database
                .get(Session.table)
                .query(
                  Q.where('appointment_id', appointment.id),
                  Q.where(
                    'session_date',
                    moment(appointment.date).format('YYYY-MM-DD'),
                  ),
                  Q.where('deleted_at', null),
                  ...clientQueries,
                  ...statusQuery,
                  ...queries,
                )
                .fetch();

              if (apptSessions.length > 0) {
                const session = apptSessions[0];
                let sessionCpt = session?.cpt || [];
                let participantCpt = staffParticipant?.cpt || [];
                if (
                  !filters?.types?.length ||
                  filters.types.some((type: string) =>
                    [...sessionCpt, ...participantCpt].includes(type),
                  )
                ) {
                  let cptLabels = '';
                  if (session?.cpt) {
                    cptLabels = session?.cpt
                      .map((cptCode: string) => {
                        const cptEntry = cptCodes.find(
                          cpt => cpt.value === cptCode,
                        );
                        return cptEntry
                          ? `${cptCode} - ${cptEntry.label}`
                          : cptCode;
                      })
                      .join(', ');
                  }
                  let status = session?.submissionTimestamp
                    ? 'Completed'
                    : session?.endTimestamp && !session?.submissionTimestamp
                    ? 'Missing Values'
                    : !session?.startTimestamp
                    ? 'Not Started'
                    : 'In-Progress';
                  let time =
                    status === 'Not Started'
                      ? 'Not Started'
                      : status === 'In-Progress'
                      ? 'In-Progress'
                      : `${moment(
                          session?.editedStartTimestamp
                            ? session?.editedStartTimestamp
                            : session?.startTimestamp,
                        ).format('hh:mm A')} - ${moment(
                          session?.editedEndTimestamp
                            ? session?.editedEndTimestamp
                            : session?.endTimestamp,
                        ).format('hh:mm A')}`;
                  let client = await database
                    .get(Patient.table)
                    .find(session?.patientId);
                  let startTimestamp = session?.editedStartTimestamp
                    ? session?.editedStartTimestamp
                    : session?.startTimestamp;
                  let endTimestamp = session?.editedEndTimestamp
                    ? session?.editedEndTimestamp
                    : session?.endTimestamp;

                  let duration = endTimestamp
                    ? Math.abs(
                        moment()
                          .set({
                            hour: endTimestamp.getHours(),
                            minute: endTimestamp.getMinutes(),
                            second: endTimestamp.getSeconds(),
                          })
                          .toDate()
                          .getTime() -
                          moment()
                            .set({
                              hour: startTimestamp.getHours(),
                              minute: startTimestamp.getMinutes(),
                              second: startTimestamp.getSeconds(),
                            })
                            .toDate()
                            .getTime(),
                      ) / 36e5
                    : 0;

                  sessionsQueried.push([
                    moment(session?.sessionDate).format('MM/DD/YYYY'),
                    time,
                    user ? `${user?.firstName} ${user?.lastName}` : '',
                    credential?.npi,
                    `${client.firstName} ${client.lastName}`,
                    client.identifier,
                    cptLabels,
                    Location(session?.location),
                    status,
                    parseFloat(duration.toFixed(2)),
                    parseFloat((duration * 60).toFixed(2)),
                    parseFloat(duration.toFixed(2)) * 4,
                  ]);
                  curatedDuration += duration;
                }
              }
            }
          }
        }
      }
    }

    setTotalDuration(curatedDuration);
    setItems(sessionsQueried.sort((a, b) => new Date(b[0]) - new Date(a[0])));
    setLoading(false);
  };

  useEffect(() => {
    setTimesheetItems([clientTabsTitles, ...items]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    setTotalDuration(0);
    recalculateAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const renderItem = useCallback(
    ({item}: any) => (
      <View style={[styles.card]}>
        <View
          style={[
            styles.flex,
            styles.row,
            styles.justifySpaceBetween,
            styles.alignItemsCenter,
          ]}>
          {item.map((value: any, index: number) => (
            <View
              key={index}
              style={[styles.flex, styles.column, styles.justifyCenter]}>
              <Text
                style={[
                  styles.paddingVertical,
                  styles.textAlignCenter,
                  Typography.P3,
                  styles.textColorSecondary,
                ]}>
                {value}
              </Text>
            </View>
          ))}
        </View>
      </View>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <Spinner visible={loading} />
      <View
        style={[
          styles.row,
          styles.justifySpaceBetween,
          styles.paddingLVertical,
          styles.paddingHorizontal,
        ]}>
        {clientTabsTitles.map((title: any, key) => (
          <View
            key={key}
            style={[styles.flex, styles.alignCenter, styles.justifyCenter]}>
            <Text
              style={[
                styles.textAlignCenter,
                Typography.P2_MEDIUM,
                styles.textColorPrimary,
              ]}>
              {title}
            </Text>
          </View>
        ))}
      </View>
      {items.length !== 0 ? (
        <>
          <FlatList
            scrollEnabled={true}
            data={items}
            style={[styles.height]}
            renderItem={renderItem}
          />
          <View
            style={[
              styles.row,
              styles.justifySpaceBetween,
              styles.paddingLVertical,
              styles.paddingHorizontal,
            ]}>
            <View style={[styles.flex, styles.marginHorizontal]}>
              <Text
                style={[
                  Typography.P3_MEDIUM,
                  styles.textColorPrimary,
                  styles.paddingLeft,
                ]}>
                {translations('totals')}
              </Text>
            </View>
            <View style={[styles.flex]} />
            <View style={[styles.flex]} />
            <View style={[styles.flex]} />
            <View style={[styles.flex]} />
            <View style={[styles.flex]} />
            <View style={[styles.flex]} />
            <View style={[styles.flex]} />
            <View style={[styles.flex]} />
            <View
              style={[
                styles.flex,
                styles.textAlignCenter,
                styles.justifySpaceBetween,
              ]}>
              <Text style={[Typography.P3_MEDIUM, styles.textColorPrimary]}>
                {translations('time_worked_hrs')}
              </Text>
              <Text style={[Typography.P3_MEDIUM, styles.textColorSecondary]}>
                {parseFloat(totalDuration.toFixed(2))}
              </Text>
            </View>
            <View
              style={[
                styles.flex,
                styles.textAlignCenter,
                styles.justifySpaceBetween,
              ]}>
              <Text style={[Typography.P3_MEDIUM, styles.textColorPrimary]}>
                {translations('time_worked_mins')}
              </Text>
              <Text style={[Typography.P3_MEDIUM, styles.textColorSecondary]}>
                {parseFloat((totalDuration * 60).toFixed(2))}
              </Text>
            </View>
            <View
              style={[
                styles.flex,
                styles.textAlignCenter,
                styles.justifySpaceBetween,
              ]}>
              <Text style={[Typography.P3_MEDIUM, styles.textColorPrimary]}>
                {translations('units')}
              </Text>
              <Text style={[Typography.P3_MEDIUM, styles.textColorSecondary]}>
                {parseFloat(totalDuration.toFixed(2)) * 4}
              </Text>
            </View>
          </View>
        </>
      ) : (
        <NoResults message={translations('empty_timesheet_message')} />
      )}
    </>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables(['authentication'], ({database, authentication}: any) => ({
    profile: authentication.userId
      ? database.get(User.table).findAndObserve(authentication.userId)
      : of(),
  })),
  withObservables([], ({profile}: any) => {
    return {
      role: profile.role,
    };
  }),
  withObservables([], ({role, profile}: any) => {
    if (role?.patientAssignedOnly) {
      return {
        assignedClients: profile.patients,
      };
    }
    return {
      assignedClients: of([]),
    };
  }),
)(TimesheetClients);
